// Delivery of events and consent updates to whichever Google tag is loaded.
// DOM-free: callers pass `window` in, tests pass a fake.
import type { GoogleTag } from "@workspace/model"

import type { ConsentModeParams } from "./consent.ts"
import type { AnalyticsEvent } from "./types.ts"

/** Set on `window` by the bootstrap once consent defaults are queued. */
export const TAG_READY_FLAG = "__itdTagReady"
/** Dispatched on `window` right after the flag is set. */
export const TAG_READY_EVENT = "itd:tag-ready"

export interface GtagWindow {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  [TAG_READY_FLAG]?: boolean
}

/** True once the bootstrap ran — before that, events must wait (see bootstrap.ts). */
export function isTagReady(win: GtagWindow): boolean {
  return win[TAG_READY_FLAG] === true
}

/**
 * Calls `gtag(...)`, or — when the bootstrap hasn't run yet — queues the call
 * exactly as the gtag shim would: an `arguments` object on the dataLayer.
 * gtag.js only treats `arguments` objects (not arrays) as commands.
 */
function gtag(win: GtagWindow, ...args: unknown[]): void {
  if (typeof win.gtag === "function") {
    win.gtag(...args)
    return
  }
  ;(win.dataLayer ??= []).push(argumentsOf(...args))
}

// The parameter list only types the call; the body returns the real
// `arguments` object, which gtag.js needs.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function argumentsOf(..._args: unknown[]): IArguments {
  // eslint-disable-next-line prefer-rest-params
  return arguments
}

/**
 * GTM: the GA4-recommended `{ecommerce: null}` reset, then the event object
 * (container triggers listen on `event`). GA4: `gtag("event", …)`.
 * Never throws — analytics must not break a diner flow.
 */
export function emitEvent(
  win: GtagWindow,
  tag: GoogleTag,
  event: AnalyticsEvent
): void {
  try {
    if (tag.kind === "gtm") {
      const dataLayer = (win.dataLayer ??= [])
      dataLayer.push({ ecommerce: null })
      dataLayer.push({ event: event.name, ecommerce: event.ecommerce })
    } else {
      gtag(win, "event", event.name, event.ecommerce)
    }
  } catch {
    // Swallow: a broken third-party tag must not surface to the diner.
  }
}

/** `consent update` — the same command for GTM and GA4. */
export function emitConsentUpdate(
  win: GtagWindow,
  params: ConsentModeParams
): void {
  try {
    gtag(win, "consent", "update", params)
  } catch {
    // Swallow, as above.
  }
}
